import { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import { RefreshCw, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import api, { formatApiError } from "@/lib/api";

export default function DevEmailLog() {
  const [entries, setEntries] = useState([]);
  const [testMode, setTestMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [limit, setLimit] = useState(50);

  const load = () => {
    setLoading(true);
    api.get(`/admin/email-log?limit=${limit}`)
      .then((r) => {
        setEntries((r.data?.entries || []).slice().reverse()); // newest first
        setTestMode(!!r.data?.test_mode);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(load, [limit]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Layout>
      <Toaster position="bottom-right" richColors />
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <Link to="/admin/users" className="text-xs text-[#7a1f2b] hover:underline inline-flex items-center gap-1 mb-2" data-testid="back-to-users">
            <ArrowLeft size={12} /> Back to users
          </Link>
          <div className="text-[11px] uppercase tracking-widest text-[#2d6b6b] font-semibold">Super Admin · Preview only</div>
          <h1 className="font-serif text-3xl sm:text-4xl font-bold text-[#1f2320]">Dev Email Log</h1>
          <p className="text-sm text-[#4a524c] mt-1">
            {testMode
              ? "EMAIL_TEST_MODE is ON — every outbound email is suppressed and logged here."
              : "EMAIL_TEST_MODE is OFF — this log is only populated when suppression is active."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select className="input-vgws py-2 text-sm" value={limit}
                   onChange={(e) => setLimit(parseInt(e.target.value, 10))}
                   data-testid="limit-select">
            {[25, 50, 100, 200, 500].map((n) => <option key={n} value={n}>Last {n}</option>)}
          </select>
          <button className="btn-vgws" onClick={load} data-testid="refresh-email-log">
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      <div className="sheet-plain overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-[#6e7770]">Loading…</div>
        ) : entries.length === 0 ? (
          <div className="p-10 text-center text-[#6e7770]">No suppressed emails yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#f6efe0] text-[#4a524c] text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-3 py-2 text-left">Sent At</th>
                  <th className="px-3 py-2 text-left">To</th>
                  <th className="px-3 py-2 text-left">CC</th>
                  <th className="px-3 py-2 text-left">Subject</th>
                  <th className="px-3 py-2 text-left">Ref</th>
                  <th className="px-3 py-2 text-left">Body snippet</th>
                </tr>
              </thead>
              <tbody data-testid="email-log-table">
                {entries.map((e, i) => (
                  <tr key={i} className="border-t border-[#efe5cd]" data-testid={`email-log-row-${i}`}>
                    <td className="px-3 py-2 font-mono text-xs text-[#4a524c] whitespace-nowrap">{e.sent_at}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{(e.to || []).join(", ")}</td>
                    <td className="px-3 py-2 text-[#6e7770] whitespace-nowrap">{(e.cc || []).join(", ") || "—"}</td>
                    <td className="px-3 py-2 max-w-[320px] truncate" title={e.subject}>{e.subject}</td>
                    <td className="px-3 py-2 font-mono text-xs">{e.ref_code || "—"}</td>
                    <td className="px-3 py-2 max-w-[320px] truncate text-[#6e7770]" title={e.body_snippet}>
                      {(e.body_snippet || "").replace(/<[^>]+>/g, "").slice(0, 100)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
